import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { TokenInformation } from '@core/models/auth.model';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth/auth.service';
import { ClientConfigService } from '@app/layout/service/client-config/client-config.service';
import { PermissionsService } from '@core/services/permissions/permissions.service';
import { SessionService } from '@core/services/session/session.service';
import { jwtDecode } from 'jwt-decode';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'd2d-impersonation-banner',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  template: `
    @if (sessionService.isImpersonating()) {
      <div
        class="fixed bottom-0 left-0 right-0 z-[2000] bg-orange-500 text-white px-4 py-2 flex items-center justify-between shadow-[0_-2px_8px_rgba(0,0,0,0.2)]"
        role="alert"
        aria-live="assertive"
      >
        <div class="flex items-center gap-3 text-sm font-medium">
          <i class="pi pi-exclamation-triangle text-base"></i>
          <span>
            Sesión impersonalizada — actuando como
            <strong>{{ targetEmail() }}</strong>
            @if (countdown() !== null) {
              · Expira en <strong>{{ countdownLabel() }}</strong>
            }
          </span>
        </div>

        <p-button
          label="Salir de impersonación"
          icon="pi pi-sign-out"
          severity="contrast"
          size="small"
          [loading]="exiting()"
          (onClick)="exitImpersonation()"
        />
      </div>
    }
  `,
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {
  readonly sessionService = inject(SessionService);
  private readonly authService = inject(AuthService);
  private readonly clientConfigService = inject(ClientConfigService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly router = inject(Router);

  readonly exiting = signal(false);
  readonly countdown = signal<number | null>(null);

  private _timer: ReturnType<typeof setInterval> | null = null;

  readonly targetEmail = computed(() => {
    const session = this.sessionService.session();
    return session?.email ?? 'usuario desconocido';
  });

  readonly countdownLabel = computed(() => {
    const secs = this.countdown();
    if (secs === null || secs <= 0) return '0:00';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  });

  ngOnInit(): void {
    this._startCountdown();
  }

  ngOnDestroy(): void {
    this._clearTimer();
  }

  private _startCountdown(): void {
    const session = this.sessionService.session();
    if (!session?.token) return;

    try {
      const decoded = jwtDecode<TokenInformation>(session.token);
      if (!decoded.is_impersonation || !decoded.exp) return;

      const update = () => {
        const remaining = Math.max(0, decoded.exp! - Math.floor(Date.now() / 1000));
        this.countdown.set(remaining);
        if (remaining <= 0) this._clearTimer();
      };

      update();
      this._timer = setInterval(update, 1000);
    } catch {
      // non-impersonation token or decode error – no countdown
    }
  }

  private _clearTimer(): void {
    if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  exitImpersonation(): void {
    if (this.exiting()) return;
    this.exiting.set(true);

    const finish = async () => {
      this._clearTimer();
      this.sessionService.endImpersonationSession();
      // Restore original CTO session: CTOs are internal users with no client config
      this.clientConfigService.unSetConfig();
      await this.permissionsService.loadEffectivePermissions();
      this.exiting.set(false);
      await this.router.navigate(['/home']);
    };

    this.authService.endImpersonation().subscribe({
      next: () => finish(),
      error: () => finish(),
    });
  }
}
